import React, { useState } from 'react';
import { HelpTopic, getTopicById } from '../data/helpContent';
import styles from '../styles/helpSystem.module.css';

interface HelpTopicViewProps {
  topic: HelpTopic;
  showNavigation?: boolean;
  onTopicClick?: (topicId: string) => void;
}

const HelpTopicView: React.FC<HelpTopicViewProps> = ({ topic, showNavigation = false, onTopicClick }) => {
  const [imageView, setImageView] = useState<'desktop' | 'mobile'>('desktop');
  
  const hasDesktop = !!topic.images.desktop;
  const hasMobile = !!topic.images.mobile;
  const hasBothViews = hasDesktop && hasMobile;

  const currentImage = imageView === 'mobile' && hasMobile 
    ? topic.images.mobile 
    : topic.images.desktop;

  const handleRelatedTopicClick = (topicId: string) => {
    if (onTopicClick) {
      onTopicClick(topicId);
    }
  };

  return (
    <div className={styles.topicView}>
      <div className={styles.topicHeader}>
        <h2 className={styles.topicTitle}>{topic.title}</h2>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection}>
        <div className={styles.description}>
          <p>{topic.description}</p>
        </div>

        {topic.details && (
          <div className={styles.details}>
            <p>{topic.details}</p>
          </div>
        )}

        {/* Image Viewer - moved after description/details */}
        {currentImage && (
          <div className={styles.imageSection}>
            <div className={styles.imageContainer}>
              <img
                src={`/help-images/${currentImage}`}
                alt={topic.title}
                className={styles.helpImage}
              />
            </div>
            {hasBothViews && (
              <div className={styles.viewToggle}>
                <button
                  className={`${styles.viewButton} ${imageView === 'desktop' ? styles.active : ''}`}
                  onClick={() => setImageView('desktop')}
                >
                  💻 Desktop
                </button>
                <button
                  className={`${styles.viewButton} ${imageView === 'mobile' ? styles.active : ''}`}
                  onClick={() => setImageView('mobile')}
                >
                  📱 Mobile
                </button>
              </div>
            )}
          </div>
        )}

        {topic.steps && topic.steps.length > 0 && (
          <div className={styles.stepsSection}>
            <h3 className={styles.sectionTitle}>How to Use:</h3>
            <ol className={styles.stepsList}>
              {topic.steps.map((step, index) => (
                <li key={index} className={styles.step}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {topic.tips && topic.tips.length > 0 && (
          <div className={styles.tipsSection}>
            <h3 className={styles.sectionTitle}>💡 Tips:</h3>
            <ul className={styles.tipsList}>
              {topic.tips.map((tip, index) => (
                <li key={index} className={styles.tip}>{tip}</li>
              ))}
            </ul>
          </div>
        )}

        {topic.relatedTopics && topic.relatedTopics.length > 0 && (
          <div className={styles.relatedSection}>
            <h3 className={styles.sectionTitle}>Related Topics:</h3>
            <div className={styles.relatedTopics}>
              {topic.relatedTopics.map(topicId => {
                const relatedTopic = getTopicById(topicId);
                if (!relatedTopic) return null;
                return (
                  <button
                    key={topicId}
                    className={styles.relatedTopicButton}
                    onClick={() => handleRelatedTopicClick(topicId)}
                  >
                    {relatedTopic.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpTopicView;

